"use client";

/**
 * useUploadValidation — file type, size, and duration validation.
 */

import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  isSupportedFormat,
  getMediaCategory,
  MIME_TO_EXTENSION,
  MAX_DURATION_SECONDS,
  FREE_PLAN_MAX_FILE_SIZE,
  PDF_MAX_FILE_SIZE,
} from "../supported-formats";
import { calculateMediaCost, calculateTextCost, calculateTextTranslationCost } from "../cost-calculator";
import { useFileMetadata } from "./use-file-metadata";
import { TRANSLATION_LANGUAGES } from "@/lib/utils/language";

export interface UploadFileItem {
  id: string;
  file: File;
  fileName: string;
  extension: string;
  mediaCategory: "audio" | "video" | "text";
  /** MediaType enum value for API calls */
  mediaType: string;
  duration: number;
  amount: number;
  /** Extracted character count for text files (PDF/DOCX/TXT). undefined for audio/video. */
  characters?: number;
  folderId: string;
  sourceLanguage: string;
  targetLanguage: string;
  enableDubbing: boolean;
  isValid: boolean;
  errorMessage?: string;
  /**
   * Set when a failed upload is safe to auto-retry (a transient network
   * failure, or an in-flight upload aborted by handleOffline() — NOT a
   * deterministic rejection like size_limit/content_type, which would only
   * fail again). Auto-resume-on-reconnect uses this to re-include the file
   * even though `isValid` is false; `uploadSingleFile` clears it the moment
   * a fresh attempt begins.
   */
  retriable?: boolean;
  /** Upload progress 0-100 */
  percent: number;
  isUploaded: boolean;
  /** S3 upload metadata after upload */
  s3?: { bucket: string; key: string; location: string };
}

export interface ValidationResult {
  isValid: boolean;
  errorMessage?: string;
  /** Categorized reason when isValid === false. Drives File Rejected funnel tracking. */
  rejectionReason?: "type" | "size" | "duration";
  fileItem?: UploadFileItem;
}

interface ValidationOptions {
  isFreePlan: boolean;
  insightCost: number;
  folderId: string;
  sourceLanguage: string;
  /** Plan-specific max file size in bytes. Null means no limit. */
  maxFileSize: number | null;
}

interface UseUploadValidationOptions {
  mode?: "upload" | "translate";
  defaultTargetLanguage?: string;
}

const CHARS_PER_SQ_INCH = 500;
const MIN_CHARS_PER_PAGE = 500;
const FALLBACK_CHARS_PER_KB = 600;
const MIN_FALLBACK_CHARS = 1000;
const POINTS_PER_INCH = 72;

function sizeFallbackChars(bytes: number): number {
  const kb = bytes / 1024;
  return Math.max(Math.round(kb * FALLBACK_CHARS_PER_KB), MIN_FALLBACK_CHARS);
}

async function extractPdfChars(file: File): Promise<number> {
  try {
    const { PDFDocument } = await import("pdf-lib");
    const buf = await file.arrayBuffer();
    const doc = await PDFDocument.load(buf);
    const pages = doc.getPages();
    let total = 0;
    for (const page of pages) {
      const { width, height } = page.getSize();
      const sqInch = (width / POINTS_PER_INCH) * (height / POINTS_PER_INCH);
      total += Math.max(Math.round(sqInch * CHARS_PER_SQ_INCH), MIN_CHARS_PER_PAGE);
    }
    return total > 0 ? total : sizeFallbackChars(file.size);
  } catch {
    return sizeFallbackChars(file.size);
  }
}

async function extractDocxChars(file: File): Promise<number> {
  try {
    const { default: mammoth } = await import("mammoth");
    const buf = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer: buf });
    return result.value.length || sizeFallbackChars(file.size);
  } catch {
    return sizeFallbackChars(file.size);
  }
}

async function extractTxtChars(file: File): Promise<number> {
  return new Promise<number>((resolve) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve(typeof reader.result === "string" ? reader.result.length : sizeFallbackChars(file.size));
    reader.onerror = () => resolve(sizeFallbackChars(file.size));
    reader.readAsText(file);
  });
}

async function extractTextCharacters(file: File): Promise<number> {
  if (file.type === "application/pdf") return extractPdfChars(file);
  if (file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") {
    return extractDocxChars(file);
  }
  if (file.type === "text/plain") return extractTxtChars(file);
  return sizeFallbackChars(file.size);
}

export function useUploadValidation({ mode = "upload", defaultTargetLanguage = "" }: UseUploadValidationOptions = {}) {
  const { t } = useTranslation();
  const { getDuration } = useFileMetadata();

  const validateFile = useCallback(
    async (
      file: File,
      options: ValidationOptions
    ): Promise<ValidationResult> => {
      const { isFreePlan, insightCost, folderId, sourceLanguage, maxFileSize } =
        options;

      if (!isSupportedFormat(file.type)) {
        return {
          isValid: false,
          rejectionReason: "type",
          errorMessage: `File type not supported: ${file.type || "unknown"}`,
        };
      }

      if (maxFileSize && file.size > maxFileSize) {
        return {
          isValid: false,
          rejectionReason: "size",
          errorMessage: `File size exceeds the maximum allowed (${Math.round(maxFileSize / (1024 * 1024))} MB).`,
        };
      }

      if (isFreePlan && file.size > FREE_PLAN_MAX_FILE_SIZE) {
        return {
          isValid: false,
          rejectionReason: "size",
          errorMessage: "Please subscribe to upload files larger than 2 GB.",
        };
      }

      const category = getMediaCategory(file.type)!;

      const extension =
        MIME_TO_EXTENSION[file.type] ||
        file.name.split(".").pop()?.toLowerCase() ||
        "";

      const mediaType = category === "text" ? "text" : category;

      let duration = 0;
      let amount = 0;
      let characters: number | undefined;

      if (category === "text") {
        if (file.type === "application/pdf" && file.size > PDF_MAX_FILE_SIZE) {
          return {
            isValid: false,
            rejectionReason: "size",
            errorMessage: t("UPLOAD.PDF_TOO_LARGE", "PDF files must be 50 MB or smaller."),
          };
        }
        characters = await extractTextCharacters(file);
        const uploadCost = calculateTextCost();
        const translationCost = mode === "translate" ? calculateTextTranslationCost(characters) : 0;
        amount = parseFloat((uploadCost + translationCost).toFixed(2));
      } else {
        duration = await getDuration(file);

        if (duration > MAX_DURATION_SECONDS) {
          return {
            isValid: false,
            rejectionReason: "duration",
            errorMessage: "File duration should be less than 10 hours.",
          };
        }

        // Duration extraction failed — paid plans still upload at $0; free plans must reject.
        if (duration === 0 && isFreePlan && file.size > 0) {
          return {
            isValid: false,
            rejectionReason: "duration",
            errorMessage: "File duration not found.",
          };
        }

        amount = calculateMediaCost(duration, insightCost);
      }

      const fileName = file.name.split(".").slice(0, -1).join(".") || file.name;

      const fileItem: UploadFileItem = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        file,
        fileName,
        extension,
        mediaCategory: category,
        mediaType,
        duration,
        amount,
        characters,
        folderId,
        sourceLanguage,
        targetLanguage: defaultTargetLanguage,
        enableDubbing: false,
        isValid: true,
        percent: 0,
        isUploaded: false,
      };

      return { isValid: true, fileItem };
    },
    [getDuration, mode, t]
  );

  /**
   * Per-row validation at submit time in translate mode. Source/target same-language
   * check normalises BCP-47 codes (`en-US` → `en`); allowlist check prevents
   * tampered targetLanguage values from reaching the payload.
   *
   * @returns map of rowId → error code; empty map means the batch is valid.
   */
  const validateBatch = useCallback(
    (
      rows: ReadonlyArray<{
        id: string;
        mediaCategory: "audio" | "video" | "text";
        sourceLanguage?: string;
        targetLanguage?: string;
      }>,
    ): Record<string, string> => {
      if (mode !== "translate") return {};
      const errors: Record<string, string> = {};

      for (const row of rows) {
        // Every translatable row — audio, video, text — requires a target language
        // before submit. Text was previously skipped; that gap is now closed so the
        // submit gate is uniform across media types.
        if (!row.targetLanguage) {
          errors[row.id] = "TARGET_LANGUAGE_REQUIRED";
          continue;
        }
        const isAllowed = TRANSLATION_LANGUAGES.some(
          (l) => l.transcriptionCode === row.targetLanguage
        );
        if (!isAllowed) {
          errors[row.id] = "INVALID_TARGET_LANGUAGE";
          continue;
        }
        const sourceNormalized = row.sourceLanguage?.split("-")[0] ?? "";
        const targetNormalized = row.targetLanguage.split("-")[0];
        if (sourceNormalized && sourceNormalized === targetNormalized) {
          errors[row.id] = "SOURCE_TARGET_SAME";
        }
      }
      return errors;
    },
    [mode]
  );

  return { validateFile, validateBatch };
}
